import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { runningAsApp } from "@/lib/runningAsApp";

const LAST_PAGE_KEY = "yarn-app-last-page";
const OPENED_KEY = "yarn-app-opened";

function attempt<T>(work: () => T): T | null {
  try {
    return work();
  } catch {
    return null;
  }
}

/**
 * A phone often closes an app in the background, and it always restarts on the homepage.
 * So the first page of a launch returns to where they were. Going home later stays home.
 */
export function useResumeInApp(): void {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!runningAsApp()) {
      return;
    }
    const firstPage = attempt(() => sessionStorage.getItem(OPENED_KEY)) === null;
    attempt(() => sessionStorage.setItem(OPENED_KEY, "1"));
    const last = attempt(() => localStorage.getItem(LAST_PAGE_KEY));
    if (firstPage && pathname === "/" && search === "" && last !== null && last !== "/") {
      navigate(last, { replace: true });
    }
    // Only when the app starts: after that, the address changes because they chose to go there.
  }, []);

  useEffect(() => {
    if (runningAsApp()) {
      attempt(() => localStorage.setItem(LAST_PAGE_KEY, `${pathname}${search}`));
    }
  }, [pathname, search]);
}
